import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, message, Spin, Form, Input, InputNumber, Upload, Image, Popconfirm, Tabs, Badge, Tooltip, Empty } from 'antd';
import {
  ArrowLeftOutlined, PlusOutlined, DeleteOutlined, UploadOutlined,
  PictureOutlined, MenuOutlined, SaveOutlined,
  CaretUpOutlined, CaretDownOutlined, CopyOutlined,
  FileExcelOutlined, ImportOutlined, EditOutlined,
} from '@ant-design/icons';
import API from '../api/client';
import { BACKEND_URL } from '../../config';

const MenuPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const [, forceUpdate] = useState(0);
  const [activeTab, setActiveTab] = useState('0');
  const [excelLoading, setExcelLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);

  const emptyItem = { name_tr: '', name_en: '', price: '', description_tr: '', description_en: '', image: '' };
  const emptyCategory = { name_tr: '', name_en: '', description_tr: '', description_en: '', image: '', items: [{ ...emptyItem }] };

  useEffect(() => {
    const load = async () => {
      try {
        const bizRes = await API.get('/businesses');
        const biz = bizRes.data.find(b => b._id === id);
        if (!biz) { message.error('İşletme bulunamadı'); navigate('/admin/businesses'); return; }
        setBusiness(biz);

        const menuRes = await API.get(`/menu/${id}`);
        if (menuRes.data && menuRes.data._id) {
          const cats = (menuRes.data.categories || []).map(cat => ({
            name_tr: cat.name_tr || cat.name || '',
            name_en: cat.name_en || '',
            description_tr: cat.description_tr || cat.description || '',
            description_en: cat.description_en || '',
            image: cat.image || '',
            items: (cat.items || []).map(item => ({
              name_tr: item.name_tr || item.name || '',
              name_en: item.name_en || '',
              price: item.price ?? item.price_tr ?? '',
              description_tr: item.description_tr || item.description || '',
              description_en: item.description_en || '',
              image: item.image || '',
            })),
          }));
          form.setFieldsValue({ categories: cats });
        } else {
          form.setFieldsValue({ categories: [{ ...emptyCategory }] });
        }
      } catch {
        message.error('Veriler yüklenemedi');
      }
      setLoading(false);
    };
    load();
  }, [id, navigate, form]);

  const handleUploadImage = async (file, categoryIndex, itemIndex) => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await API.post('/upload', fd);
      const url = `${BACKEND_URL}${res.data.url}`;
      const categories = form.getFieldValue('categories');
      categories[categoryIndex].items[itemIndex].image = url;
      form.setFieldsValue({ categories: [...categories] });
      forceUpdate(n => n + 1);
      message.success('Görsel yüklendi');
    } catch { message.error('Görsel yüklenemedi'); }
    return false;
  };

  const handleUploadCategoryImage = async (file, categoryIndex) => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await API.post('/upload', fd);
      const url = `${BACKEND_URL}${res.data.url}`;
      const categories = form.getFieldValue('categories');
      categories[categoryIndex].image = url;
      form.setFieldsValue({ categories: [...categories] });
      forceUpdate(n => n + 1);
      message.success('Kategori görseli yüklendi');
    } catch { message.error('Görsel yüklenemedi'); }
    return false;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const values = form.getFieldsValue(true);
      await API.put(`/menu/${id}`, values);
      message.success('Menü kaydedildi');
    } catch (err) {
      console.error('Kayıt hatası:', err);
      message.error('Kayıt sırasında hata oluştu');
    }
    setSaving(false);
  };

  const handleExportExcel = async () => {
    setExcelLoading(true);
    try {
      const res = await API.get(`/menu/${id}/export-excel`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${business.name}_menu.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      message.success('Excel indirildi!');
    } catch { message.error('Excel dışa aktarılamadı'); }
    setExcelLoading(false);
  };

  const handleImportExcel = async (file) => {
    setImportLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await API.post(`/menu/${id}/import-excel`, fd);
      message.success(res.data.message);
      window.location.reload();
    } catch (err) {
      message.error(err.response?.data?.detail || 'Excel içe aktarılamadı');
    }
    setImportLoading(false);
    return false;
  };

  const moveItem = (catName, itemIndex, direction) => {
    const cats = form.getFieldValue('categories');
    const items = cats[catName].items;
    const target = itemIndex + direction;
    if (target < 0 || target >= items.length) return;
    [items[itemIndex], items[target]] = [items[target], items[itemIndex]];
    form.setFieldsValue({ categories: [...cats] });
    forceUpdate(n => n + 1);
  };

  const duplicateItem = (catName, itemIndex) => {
    const cats = form.getFieldValue('categories');
    const items = cats[catName].items;
    items.splice(itemIndex + 1, 0, { ...items[itemIndex] });
    form.setFieldsValue({ categories: [...cats] });
    forceUpdate(n => n + 1);
  };

  const removeItemImage = (catName, itemIndex) => {
    const cats = form.getFieldValue('categories');
    cats[catName].items[itemIndex].image = '';
    form.setFieldsValue({ categories: [...cats] });
    forceUpdate(n => n + 1);
  };

  const removeCategoryImage = (catIndex) => {
    const cats = form.getFieldValue('categories');
    cats[catIndex].image = '';
    form.setFieldsValue({ categories: [...cats] });
    forceUpdate(n => n + 1);
  };

  const moveCategoryTab = (catIndex, direction) => {
    const cats = form.getFieldValue('categories');
    const target = catIndex + direction;
    if (target < 0 || target >= cats.length) return;
    [cats[catIndex], cats[target]] = [cats[target], cats[catIndex]];
    form.setFieldsValue({ categories: [...cats] });
    setActiveTab(String(target));
    forceUpdate(n => n + 1);
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>;
  if (!business) return null;

  const categories = form.getFieldValue('categories') || [];

  // Build tab items from categories
  const tabItems = categories.map((cat, catIndex) => {
    const itemCount = (cat.items || []).length;
    const catImage = cat.image;

    return {
      key: String(catIndex),
      label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 0' }}>
          {catImage ? (
            <img src={catImage} alt="" style={{ width: 22, height: 22, borderRadius: 4, objectFit: 'cover' }} />
          ) : (
            <MenuOutlined style={{ fontSize: 13, color: '#999' }} />
          )}
          <span>{cat.name_tr || 'Yeni Kategori'}</span>
          <Badge count={itemCount} size="small" style={{ backgroundColor: '#D4A843', marginLeft: 2 }} showZero />
        </div>
      ),
      children: (
        <div>
          {/* Category Settings Bar */}
          <div style={{
            display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap',
            padding: '12px 16px', marginBottom: 16,
            background: '#f8f8fc', borderRadius: 10, border: '1px solid #eee',
          }}>
            {/* Category image */}
            <div style={{ position: 'relative', flexShrink: 0 }}>
              {catImage ? (
                <div style={{ position: 'relative' }}>
                  <Image src={catImage} width={48} height={48}
                    style={{ objectFit: 'cover', borderRadius: 8 }} preview={false} />
                  <Button size="small" type="text" danger icon={<DeleteOutlined />}
                    onClick={() => removeCategoryImage(catIndex)}
                    style={{ position: 'absolute', top: -6, right: -6, fontSize: 10, width: 18, height: 18, padding: 0, background: '#fff', borderRadius: '50%', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }}
                  />
                </div>
              ) : (
                <Upload showUploadList={false} beforeUpload={(file) => handleUploadCategoryImage(file, catIndex)} accept="image/*">
                  <div style={{
                    width: 48, height: 48, borderRadius: 8, border: '1px dashed #ccc',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                    background: '#fff',
                  }}>
                    <PictureOutlined style={{ color: '#bbb', fontSize: 20 }} />
                  </div>
                </Upload>
              )}
            </div>

            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Form.Item name={['categories', catIndex, 'name_tr']} noStyle rules={[{ required: true }]}>
                  <Input placeholder="Kategori Adı (TR)" style={{ flex: 1, minWidth: 140, fontWeight: 600 }} size="small" />
                </Form.Item>
                <Form.Item name={['categories', catIndex, 'name_en']} noStyle>
                  <Input placeholder="Category (EN)" size="small"
                    style={{ flex: 1, minWidth: 140 }}
                    prefix={<span style={{ color: '#1890ff', fontSize: 10 }}>EN</span>} />
                </Form.Item>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Form.Item name={['categories', catIndex, 'description_tr']} noStyle>
                  <Input placeholder="Açıklama (TR) - opsiyonel" size="small" style={{ flex: 1, minWidth: 140 }} />
                </Form.Item>
                <Form.Item name={['categories', catIndex, 'description_en']} noStyle>
                  <Input placeholder="Description (EN)" size="small"
                    style={{ flex: 1, minWidth: 140 }}
                    prefix={<span style={{ color: '#1890ff', fontSize: 10 }}>EN</span>} />
                </Form.Item>
              </div>
            </div>

            <Form.Item name={['categories', catIndex, 'image']} noStyle><Input type="hidden" /></Form.Item>

            {/* Category actions */}
            <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
              <Tooltip title="Sola Taşı"><Button size="small" type="text" icon={<CaretUpOutlined style={{ transform: 'rotate(-90deg)' }} />}
                disabled={catIndex === 0} onClick={() => moveCategoryTab(catIndex, -1)} /></Tooltip>
              <Tooltip title="Sağa Taşı"><Button size="small" type="text" icon={<CaretDownOutlined style={{ transform: 'rotate(-90deg)' }} />}
                disabled={catIndex === categories.length - 1} onClick={() => moveCategoryTab(catIndex, 1)} /></Tooltip>
              {!catImage && (
                <Upload showUploadList={false} beforeUpload={(file) => handleUploadCategoryImage(file, catIndex)} accept="image/*">
                  <Tooltip title="Görsel Yükle"><Button size="small" type="text" icon={<UploadOutlined />} /></Tooltip>
                </Upload>
              )}
              <Popconfirm title="Bu kategori ve tüm ürünleri silinsin mi?" onConfirm={() => {
                const cats = form.getFieldValue('categories');
                cats.splice(catIndex, 1);
                form.setFieldsValue({ categories: [...cats] });
                setActiveTab(String(Math.max(0, catIndex - 1)));
                forceUpdate(n => n + 1);
              }}>
                <Button size="small" type="text" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            </div>
          </div>

          {/* Items */}
          <Form.List name={['categories', catIndex, 'items']}>
            {(itemFields, { add: addItem, remove: removeItem }) => (
              <>
                {itemFields.length === 0 && (
                  <Empty description="Henüz ürün eklenmemiş" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ margin: '24px 0' }} />
                )}

                {itemFields.map(({ key: itemKey, name: itemName }, itemIndex) => {
                  const itemImage = form.getFieldValue(['categories', catIndex, 'items', itemName, 'image']);
                  return (
                    <div key={itemKey} style={{
                      display: 'flex', alignItems: 'flex-start', gap: 10,
                      padding: '10px 12px', marginBottom: 6,
                      background: itemIndex % 2 === 0 ? '#fafafa' : '#fff',
                      borderRadius: 8, border: '1px solid #f0f0f0',
                      transition: 'box-shadow 0.2s',
                    }}
                      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)'}
                      onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
                    >
                      {/* Number */}
                      <div style={{ color: '#bbb', fontSize: 12, fontWeight: 600, paddingTop: 6, width: 20, textAlign: 'center', flexShrink: 0 }}>
                        {itemIndex + 1}
                      </div>

                      {/* Item image */}
                      <div style={{ flexShrink: 0 }}>
                        {itemImage ? (
                          <div style={{ position: 'relative' }}>
                            <Image src={itemImage} width={52} height={52}
                              style={{ objectFit: 'cover', borderRadius: 8 }} />
                            <Button size="small" type="text" danger icon={<DeleteOutlined />}
                              onClick={() => removeItemImage(catIndex, itemName)}
                              style={{ position: 'absolute', top: -6, right: -6, fontSize: 10, width: 18, height: 18, padding: 0, background: '#fff', borderRadius: '50%', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }}
                            />
                          </div>
                        ) : (
                          <Upload showUploadList={false}
                            beforeUpload={(file) => handleUploadImage(file, catIndex, itemName)} accept="image/*">
                            <div style={{
                              width: 52, height: 52, borderRadius: 8,
                              border: '1px dashed #d9d9d9', background: '#fafafa',
                              display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                            }}>
                              <PictureOutlined style={{ color: '#bbb', fontSize: 18 }} />
                            </div>
                          </Upload>
                        )}
                      </div>

                      {/* Item details */}
                      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <Form.Item name={[itemName, 'name_tr']} noStyle rules={[{ required: true }]}>
                            <Input placeholder="Ürün adı (TR)" size="small" style={{ fontWeight: 600, flex: 1 }} />
                          </Form.Item>
                          <Form.Item name={[itemName, 'price']} noStyle>
                            <InputNumber placeholder="₺" min={0} size="small" style={{ width: 90 }}
                              formatter={v => v ? `${v} ₺` : ''}
                              parser={v => v.replace(/\s*₺\s*/g, '')} />
                          </Form.Item>
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <Form.Item name={[itemName, 'name_en']} noStyle>
                            <Input placeholder="Product name (EN)" size="small"
                              style={{ flex: 1, fontSize: 12 }}
                              prefix={<span style={{ color: '#1890ff', fontSize: 10 }}>EN</span>} />
                          </Form.Item>
                          <Form.Item name={[itemName, 'description_tr']} noStyle>
                            <Input placeholder="Açıklama (TR)" size="small" style={{ flex: 1, fontSize: 12 }} />
                          </Form.Item>
                        </div>
                        <Form.Item name={[itemName, 'description_en']} noStyle>
                          <Input placeholder="Description (EN) - optional" size="small"
                            style={{ fontSize: 12, color: '#999' }}
                            prefix={<span style={{ color: '#1890ff', fontSize: 10 }}>EN</span>} />
                        </Form.Item>
                        <Form.Item name={[itemName, 'image']} noStyle><Input type="hidden" /></Form.Item>
                      </div>

                      {/* Actions */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flexShrink: 0 }}>
                        <Tooltip title="Yukarı" placement="left">
                          <Button size="small" type="text" icon={<CaretUpOutlined />}
                            disabled={itemIndex === 0}
                            onClick={() => moveItem(catIndex, itemIndex, -1)}
                            style={{ width: 26, height: 22, padding: 0 }} />
                        </Tooltip>
                        <Tooltip title="Aşağı" placement="left">
                          <Button size="small" type="text" icon={<CaretDownOutlined />}
                            disabled={itemIndex === itemFields.length - 1}
                            onClick={() => moveItem(catIndex, itemIndex, 1)}
                            style={{ width: 26, height: 22, padding: 0 }} />
                        </Tooltip>
                        <Tooltip title="Kopyala" placement="left">
                          <Button size="small" type="text" icon={<CopyOutlined />}
                            onClick={() => duplicateItem(catIndex, itemIndex)}
                            style={{ width: 26, height: 22, padding: 0 }} />
                        </Tooltip>
                        <Popconfirm title="Ürün silinsin mi?" onConfirm={() => removeItem(itemName)}>
                          <Button size="small" type="text" danger icon={<DeleteOutlined />}
                            style={{ width: 26, height: 22, padding: 0 }} />
                        </Popconfirm>
                      </div>
                    </div>
                  );
                })}

                <Button type="dashed" onClick={() => addItem({ ...emptyItem })} icon={<PlusOutlined />}
                  style={{ width: '100%', marginTop: 10 }}>
                  Ürün Ekle
                </Button>
              </>
            )}
          </Form.List>
        </div>
      ),
    };
  });

  // Add "+" tab for new category
  tabItems.push({
    key: 'add-new',
    label: (
      <span style={{ color: '#D4A843', fontWeight: 600 }}>
        <PlusOutlined style={{ marginRight: 4 }} /> Yeni Kategori
      </span>
    ),
    children: null,
  });

  return (
    <>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/admin/businesses')}>Geri</Button>
          <div>
            <h2 style={{ margin: 0, fontSize: 18 }}>
              <EditOutlined style={{ marginRight: 8, color: '#D4A843' }} />{business.name}
            </h2>
            <span style={{ fontSize: 12, color: '#999' }}>Menü Yönetimi · {categories.length} kategori</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Tooltip title="Menüyü Excel olarak indir">
            <Button icon={<FileExcelOutlined />} onClick={handleExportExcel} loading={excelLoading}
              style={{ color: '#217346', borderColor: '#217346' }}>
              Excel İndir
            </Button>
          </Tooltip>
          <Upload showUploadList={false} beforeUpload={handleImportExcel} accept=".xlsx,.xls">
            <Tooltip title="Excel dosyasından menüyü güncelle">
              <Button icon={<ImportOutlined />} loading={importLoading}>
                Excel Yükle
              </Button>
            </Tooltip>
          </Upload>
          <Button type="primary" size="large" icon={<SaveOutlined />} onClick={handleSave} loading={saving}>
            Kaydet
          </Button>
        </div>
      </div>

      <Form form={form} layout="vertical">
        <Tabs
          type="card"
          activeKey={activeTab}
          onChange={(key) => {
            if (key === 'add-new') {
              const cats = form.getFieldValue('categories') || [];
              cats.push({ ...emptyCategory, items: [{ ...emptyItem }] });
              form.setFieldsValue({ categories: [...cats] });
              setActiveTab(String(cats.length - 1));
              forceUpdate(n => n + 1);
            } else {
              setActiveTab(key);
            }
          }}
          items={tabItems}
          style={{ minHeight: 400 }}
          tabBarStyle={{ marginBottom: 0 }}
          tabBarGutter={4}
        />
      </Form>

      {/* Floating save on mobile */}
      <div className="mobile-save-btn" style={{ position: 'fixed', bottom: 20, right: 20, zIndex: 100, display: 'none' }}>
        <Button type="primary" shape="circle" size="large" icon={<SaveOutlined />}
          onClick={handleSave} loading={saving}
          style={{ width: 56, height: 56, boxShadow: '0 4px 16px rgba(47,35,134,0.3)' }} />
      </div>

      <style>{`
        @media (max-width: 768px) {
          .mobile-save-btn { display: block !important; }
          .ant-tabs-nav-list { overflow-x: auto; flex-wrap: nowrap; }
          .ant-tabs-tab { padding: 6px 10px !important; font-size: 13px; }
        }
      `}</style>
    </>
  );
};

export default MenuPage;
